import { useCallback } from 'react';
import type { FunctionComponent } from 'react';

import Button from '../../../generic/Button';
import { TextInput } from '../../../generic/Forms';
import { icons, IconText } from '../../../generic/Icon';

import JiraSettingsSection from './JiraSettingsSection';

export type JiraHeaderItem = {
  headerKey: string; // Named because `key` is a special prop in React.
  value: string;
};

type JiraSettingsHeadersTableRowProps = JiraHeaderItem & {
  onDelete: () => void;
  onUpdate: (item: Partial<JiraHeaderItem>) => void;
};

const JiraSettingsHeadersTableRow: FunctionComponent<JiraSettingsHeadersTableRowProps> = ({
  headerKey,
  value,
  onDelete,
  onUpdate,
}) => (
  <div className="jira-settings__headers-table__row">
    <TextInput
      label="key"
      placeholder="Key"
      size="small"
      value={headerKey}
      onChange={key => onUpdate({ headerKey: key })}
    />
    <TextInput
      label="value"
      placeholder="Value"
      size="small"
      value={value}
      onChange={newValue => onUpdate({ value: newValue })}
    />
    <div>
      <Button
        type="outline"
        size="medium"
        onClick={onDelete}
        icon={icons.trash}
      />
    </div>
  </div>
);

/**
 * Factory for creating new JiraHeaderItem.
 */
const getNewJiraHeader = (): JiraHeaderItem => ({
  headerKey: '',
  value: '',
});

export type JiraSettingsHeadersTableProps = {
  headers: JiraHeaderItem[];
  onUpdateHeaders: (headers: JiraHeaderItem[]) => void;
};

/**
 * JiraSettingsHeadersTable
 */
const JiraSettingsHeadersTable: FunctionComponent<JiraSettingsHeadersTableProps> = ({
  headers,
  onUpdateHeaders,
}) => {
  const onAddNewHeader = useCallback(() =>
    onUpdateHeaders([...headers, getNewJiraHeader()]), [onUpdateHeaders, headers]);

  const getOnDeleteHeader = useCallback((headerIndex: number) => () =>
    onUpdateHeaders(headers.filter((_header, index) => index !== headerIndex)), [onUpdateHeaders, headers]);

  const getOnUpdateHeader = useCallback((headerIndex: number) => (updatedField: Partial<JiraHeaderItem>) =>
    onUpdateHeaders(headers.map((field, index) => index !== headerIndex
      ? field
      : {
          ...field,
          ...updatedField,
        })), [onUpdateHeaders, headers]);

  return (
    <JiraSettingsSection
      heading="Headers (optional)"
      subHeading="Specify any additional headers you want attached to requests made to your Jira instance."
    >
      <div className="jira-settings__headers-table">
        {headers.length > 0 && (
          <header className="jira-settings__headers-table__header">
            <div className="jira-settings__headers-table__header__item">
              Key
            </div>
            <div className="jira-settings__headers-table__header__item">
              Value
            </div>
            <div className="jira-settings__headers-table__header__item">
              Delete
            </div>
          </header>
        )}
        {headers.map(({ headerKey, value }, index) => (
          <JiraSettingsHeadersTableRow
            headerKey={headerKey}
            value={value}
            onDelete={getOnDeleteHeader(index)}
            onUpdate={getOnUpdateHeader(index)}
          />
        ))}
        <footer className="jira-settings__headers-table__footer">
          <Button
            type="secondary"
            size="medium"
            onClick={onAddNewHeader}
          >
            <IconText
              icon={icons.plus}
              text="Add Header"
            />
          </Button>
        </footer>
      </div>
    </JiraSettingsSection>
  );
};

export default JiraSettingsHeadersTable;
